import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import VendorStoreService from "../../../services/VendorStoreService";
import Breadcrumb from "../../../components/Breadcrumb";
import ListVendorStore from "../Components/ListVendorStore";
import ViewModelVendorStore from "../Components/ViewModelVendorStore";
import MessageDialog from "../../../components/MessageDialog";

export default function ViewVendorStore() {
  const [vendorStores, setVendorStores] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const navigate = useNavigate();
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await VendorStoreService.getAll(currentPage, itemsPerPage, searchText);
      setVendorStores(res?.data || []);
      setPageCount(res?.totalPages || 1);
    } catch (err) {
      console.error(err);
      showMessage("Error fetching vendor stores", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, searchText]);

  const showMessage = (text, type = "success") => {
    setMessage(text);
    setMsgType(type);
  };

  const handleAdd = () => {
    navigate("/cpnl/vendorstore/add");
  };

  const handleModify = () => {
    if (!selected) {
      showMessage("Please select a vendor store to modify.", "error");
      return;
    }
    if (selected.status !== "Active") {
      showMessage("Inactive vendor stores cannot be modified.", "error");
      return;
    }
    navigate(`/cpnl/vendorstore/add/${selected.id}`);
  };

  const handleView = async () => {
    if (!selected) {
      showMessage("Please select a vendor store to view.", "error");
      return;
    }
    try {
      setLoading(true);
      const res = await VendorStoreService.getById(selected.id);
      const data = res?.data || res;
      if (!data) {
        showMessage("Failed to load vendor store details.", "error");
        return;
      }
      setViewData(data);
      setShowViewModal(true);
    } catch (err) {
      console.error(err);
      showMessage("Error loading vendor store details.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
    const store = vendorStores.find((s) => s.id === id);
    if (!store) return;

    const confirmMsg =
      store.status === "Active"
        ? "Are you sure you want to change status to Inactive?"
        : "Are you sure you want to change status to Active?";

    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    try {
      await VendorStoreService.updateStatus(id);
      showMessage(
        store.status === "Active"
          ? "Vendor Store inactivated successfully!"
          : "Vendor Store activated successfully!",
        "success"
      );
      fetchData();
    } catch (err) {
      console.error(err);
      showMessage("Error updating status.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (event) => {
    setCurrentPage(event.selected + 1);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewData(null);
  };

  return (
    <>
      <Breadcrumb pageTitle="Vendor Store" secondTitle="Masters" />

      <div className="row">
        <div className="card py-2 bg-white shadow-sm">
          <MessageDialog
            message={message}
            type={msgType}
            setMessage={setMessage}
            setMsgType={setMsgType}
          />

          <div className="mb-3 pb-2 border-bottom d-flex align-items-end gap-3">
            <div>
              <input
                type="text"
                className="form-control"
                placeholder="Search Vendor Store"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="ms-auto d-flex gap-2">
              <button className="btn btn-success" onClick={handleAdd} disabled={loading}>
                <i className="ti ti-plus me-1"></i> Add
              </button>
              <button className="btn btn-warning" onClick={handleModify} disabled={loading}>
                <i className="ti ti-pencil me-1"></i> Modify
              </button>
              <button className="btn btn-primary" onClick={handleView} disabled={loading}>
                <i className="ti ti-eye me-1"></i> View
              </button>
            </div>
          </div>

          <ListVendorStore
            loading={loading}
            vendorStores={vendorStores}
            selected={selected}
            setSelected={setSelected}
            handleToggleStatus={handleToggleStatus}
            pageCount={pageCount}
            handlePageClick={handlePageClick}
          />
        </div>
      </div>

      <ViewModelVendorStore
        showViewModal={showViewModal}
        viewData={viewData}
        closeViewModal={closeViewModal}
      />
    </>
  );
}
